import { useEffect, useRef } from "react";
import { attributionApi, hasAdParams, readAdParams } from "@shared/api/attribution.api";
import { useAuth } from "@shared/contexts/auth-context";
import { readAttributionCode, saveAttributionCode } from "@shared/helper_lib/bot-link";
import { getMetrikaClientId } from "@shared/helper_lib/metrika";

/** На прокладке клик заводит сама страница — иначе получим два клика на один переход */
const TG_REDIRECT_PATH = "/tg";

/**
 * Ловит рекламные метки на входе и привязывает их к пользователю после логина.
 * Захват и привязка разнесены: в момент клика по объявлению юзер ещё не авторизован,
 * поэтому код клика ждёт в localStorage до появления сессии.
 */
export const useAdAttribution = (): void => {
  const { isAuthenticated } = useAuth();
  const clientIdRef = useRef<string | undefined>(undefined);
  const capturePromiseRef = useRef<Promise<void> | null>(null);
  const attachedRef = useRef(false);

  useEffect(() => {
    if (window.location.pathname === TG_REDIRECT_PATH) {
      return;
    }

    const capture = async (): Promise<void> => {
      try {
        clientIdRef.current = await getMetrikaClientId();

        const adParams = readAdParams(window.location.search);
        if (!hasAdParams(adParams)) {
          return;
        }

        // Свежий yclid всегда важнее сохранённого: старый мог протухнуть
        // по правилу 21 дня, и тогда конверсию не к чему было бы привязать
        if (readAttributionCode() && !adParams.yclid) {
          return;
        }

        const code = await attributionApi.createClick({
          ...adParams,
          ymClientId: clientIdRef.current,
          landingUrl: window.location.href
        });

        saveAttributionCode(code);
      } catch (error) {
        console.warn("Не удалось сохранить рекламный клик:", error);
      }
    };

    capturePromiseRef.current = capture();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || attachedRef.current) {
      return;
    }

    const attach = async (): Promise<void> => {
      // У залогиненного юзера сессия поднимается быстрее, чем успевает
      // отработать захват клика, поэтому дожидаемся его — иначе привязка
      // уйдёт без yclid и второй попытки уже не будет
      await capturePromiseRef.current;

      const code = readAttributionCode() || undefined;
      const ymClientId = clientIdRef.current ?? (await getMetrikaClientId());

      if (!code && !ymClientId) {
        return;
      }

      attachedRef.current = true;

      try {
        await attributionApi.attach({ code, ymClientId });
      } catch (error) {
        attachedRef.current = false;
        console.warn("Не удалось привязать атрибуцию:", error);
      }
    };

    void attach();
  }, [isAuthenticated]);
};
